import React, { ChangeEvent, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import CustomInput from "../components/customs/custom-input";
import CustomRadioButton from "../components/customs/custom-radio-button";
import CustomSelect from "../components/customs/custom-select";
import CustomButton from "../components/customs/custom-button";
import {
  FormSection,
  FormField,
  RadioField,
} from "../components/customs/form-section";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { useDebounceCallback } from "usehooks-ts";
import { useChaseEditModel } from "../store/use-chase-edit-model";
import ModalPortal from "../components/modal-portal";
import {
  branddata,
  loanStatusData,
} from "../components/chase-list-components/chase-list-data";

interface IEditChaseModel {
  UpdateItemData: () => void;
}

const EditChaseModel: React.FC<IEditChaseModel> = ({ UpdateItemData }) => {
  const { initialValues, onClose } = useChaseEditModel();
  const [values, setValues] = useState<Record<string, any>>({
    auto: initialValues?.data?.auto,
    validOldPaymentDetails: initialValues?.data?.validOldPaymentDetails,
  });
  const [loading, setLoading] = useState<boolean>(false);

  const updateChase = async () => {
    setLoading(true);
    try {
      await axiosInstance.patch(`/chase/${initialValues?.id}`, {
        ...values,
      });
      UpdateItemData();
      toast.success("Chase Updated Successfully");
      onClose();
      setLoading(false);
    } catch (error) {
      toast.error(ParseErrors(error));
      setLoading(false);
      console.log(error);
    }
  };

  const HandleChangeValues = useDebounceCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    200
  );

  const GetDefaultValues = (name: string) => {
    return (
      (typeof values[name] === "number"
        ? Number(values[name])?.toFixed(2)
        : values[name]) ||
      (typeof initialValues?.data[name] === "number"
        ? Number(initialValues?.data[name])?.toFixed(2)
        : initialValues?.data[name])
    );
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[99999] flex animate-scrim-in items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-6"
        onClick={onClose}
      >
        <div
          className="flex max-h-[calc(100vh-48px)] w-full max-w-[850px] animate-modal-in flex-col overflow-hidden rounded-2xl border border-edge bg-surface shadow-overlay"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ------------------------------- HEADER -------------------------------- */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-edge bg-surface-elevated px-5 py-4">
            <h2 className="text-[20px] font-bold leading-tight text-text-primary">
              Update Chase
            </h2>
            <button
              type="button"
              onClick={onClose}
              title="Close"
              aria-label="Close"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-light-gray transition-colors duration-200 hover:bg-overlay-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            >
              <FontAwesomeIcon icon={faXmark} className="text-[15px]" />
            </button>
          </div>

          {/* -------------------------------- BODY --------------------------------- */}
          <div className="custom-scrollbar flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
            <FormSection
              title="Customer"
              description="Who the renewal notices are sent to."
            >
              <FormField>
                <CustomInput
                  autoComplete="none"
                  label="Fore Name"
                  defaultValue={GetDefaultValues("forename")}
                  name="forename"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  autoComplete="none"
                  label="Surname"
                  defaultValue={GetDefaultValues("surname")}
                  name="surname"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Email Address"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("email")}
                  name="email"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Phone Number"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("telephone")}
                  name="telephone"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Date of Birth"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("dateOfBirth")}
                  name="dateOfBirth"
                  type="date"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Post Code"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("postcode")}
                  name="postcode"
                  className="uppercase"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField span={2}>
                <CustomInput
                  label="Address 1"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("address")}
                  name="address"
                  onChange={HandleChangeValues}
                />
              </FormField>
            </FormSection>

            <FormSection
              title="Policy"
              description="Reference, cover and renewal details."
            >
              <FormField>
                <CustomInput
                  label="Policy Reference Number"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("policyReferenceNumber")}
                  name="policyReferenceNumber"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Policy Number"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("policyNumber")}
                  name="policyNumber"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomSelect
                  comboboxType="select"
                  label="Chase Status"
                  labelProperty="name"
                  showTop
                  valueProperty="name"
                  options={loanStatusData}
                  onSingleSelectChange={(option) =>
                    setValues((prev) => ({ ...prev, status: option?.name }))
                  }
                  value={loanStatusData.find(
                    (type) => type.name === GetDefaultValues("status")
                  )}
                  disabled
                />
              </FormField>
              <FormField>
                <CustomSelect
                  comboboxType="select"
                  label="Brand"
                  labelProperty="label"
                  showTop
                  valueProperty="name"
                  options={branddata}
                  onSingleSelectChange={(option) =>
                    setValues((prev) => ({ ...prev, brandCode: option?.name }))
                  }
                  value={branddata.find(
                    (type) => type.name === GetDefaultValues("brandCode")
                  )}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Registration Number"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("registrationNumber")}
                  name="registrationNumber"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Insurer Name"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("insurer")}
                  name="insurer"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Renewal Date"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("renewalDate")}
                  name="renewalDate"
                  type="date"
                  onChange={HandleChangeValues}
                  disabled
                />
              </FormField>
              <FormField>
                <CustomInput
                  label="Agent Code"
                  autoComplete="none"
                  defaultValue={GetDefaultValues("agentCode")}
                  name="agentCode"
                  onChange={HandleChangeValues}
                />
              </FormField>
              <FormField span={2}>
                <RadioField label="Policy Type">
                  <CustomRadioButton
                    id="Nonstandard"
                    name="policyType"
                    label="Standard"
                    value="Standard"
                    checked={GetDefaultValues("policyType") === "Standard"}
                    onChange={HandleChangeValues}
                  />
                  <CustomRadioButton
                    id="standard"
                    name="policyType"
                    label="Non-Standard"
                    checked={GetDefaultValues("policyType") === "Non-Standard"}
                    onChange={HandleChangeValues}
                    value="Non-Standard"
                  />
                </RadioField>
              </FormField>
            </FormSection>

            <FormSection
              title="Payment &amp; Renewal"
              description="How the customer pays and whether cover renews automatically."
            >
              <FormField>
                <RadioField label="Payment Type">
                  <CustomRadioButton
                    id="cash"
                    name="paymentType"
                    label="Cash"
                    value="cash"
                    checked={GetDefaultValues("paymentType") === "cash"}
                    onChange={HandleChangeValues}
                  />
                  <CustomRadioButton
                    id="instalments"
                    name="paymentType"
                    label="Instalments"
                    checked={GetDefaultValues("paymentType") === "instalments"}
                    onChange={HandleChangeValues}
                    value="instalments"
                  />
                </RadioField>
              </FormField>
              <FormField>
                <RadioField label="Auto Renew">
                  <CustomRadioButton
                    id="auto-true"
                    name="auto"
                    label="True"
                    value="True"
                    checked={values?.auto}
                    onChange={() => {
                      setValues((prev) => ({ ...prev, auto: true }));
                    }}
                  />
                  <CustomRadioButton
                    id="auto-false"
                    name="auto"
                    label="False"
                    checked={!values?.auto}
                    onChange={() => {
                      setValues((prev) => ({ ...prev, auto: false }));
                    }}
                    value="false"
                  />
                </RadioField>
              </FormField>
              <FormField>
                <RadioField label="Valid Old Payments">
                  <CustomRadioButton
                    id="validOldPaymentDetails-true"
                    name="validOldPaymentDetails"
                    label="True"
                    value="true"
                    checked={values?.validOldPaymentDetails}
                    onChange={() => {
                      setValues((prev) => ({
                        ...prev,
                        validOldPaymentDetails: true,
                      }));
                    }}
                  />
                  <CustomRadioButton
                    id="validOldPaymentDetails-false"
                    name="validOldPaymentDetails"
                    label="False"
                    checked={!values?.validOldPaymentDetails}
                    onChange={() => {
                      setValues((prev) => ({
                        ...prev,
                        validOldPaymentDetails: false,
                      }));
                    }}
                    value="false"
                  />
                </RadioField>
              </FormField>
            </FormSection>
          </div>

          {/* ------------------------------- FOOTER -------------------------------- */}
          <div className="flex shrink-0 flex-col gap-3 border-t border-edge bg-surface-elevated px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
            <CustomButton
              variant="secondary"
              disabled={loading}
              onClick={onClose}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              loading={loading}
              disabled={loading}
              onClick={updateChase}
            >
              Update Chase
            </CustomButton>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default EditChaseModel;
